import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { WorkTypeIcon } from './KanbanBoard';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  CheckCircle, Circle, ClockCountdown, Warning, TrendUp, ArrowUp, Bug, BookOpen, CheckSquare, Flag,
} from '@phosphor-icons/react';

export default function ProjectOverview({ projectId }) {
  const { currentOrg, getOrgHeaders, API } = useAuth();
  const [data, setData] = useState(null);

  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  useEffect(() => {
    if (!currentOrg || !projectId) return;
    axios.get(`${API}/projects/${projectId}/overview`, h()).then(r => setData(r.data)).catch(() => {});
  }, [projectId, currentOrg]);

  if (!data) return <div className="flex-1 flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  const completionPct = data.total > 0 ? Math.round((data.done / data.total) * 100) : 0;

  const StatusBar = ({ label, count, total, color }) => {
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (
      <div className="flex items-center gap-3">
        <span className="text-[12px] text-zinc-600 w-24 font-medium">{label}</span>
        <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
        <span className="text-[12px] text-zinc-500 w-8 text-right font-mono">{count}</span>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="project-overview">
      <div className="max-w-[1100px] mx-auto space-y-6">
        {/* Top Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: 'Total Items', value: data.total, icon: CheckSquare, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Completed', value: data.done, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Due Soon', value: data.due_soon, icon: ClockCountdown, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Overdue', value: data.overdue, icon: Warning, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Updated (7d)', value: data.updated_recent, icon: TrendUp, color: 'text-violet-600', bg: 'bg-violet-50' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-zinc-200/80 p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold">{s.label}</span>
                <div className={`w-7 h-7 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <s.icon size={14} className={s.color} weight="duotone" />
                </div>
              </div>
              <p className="font-['Outfit'] text-2xl font-black text-zinc-900 tracking-tight">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Completion Progress */}
          <div className="bg-white rounded-xl border border-zinc-200/80 p-5">
            <h3 className="text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold mb-4">Status Overview</h3>
            <div className="flex items-center gap-4 mb-5">
              {/* Donut Chart */}
              <div className="relative w-24 h-24 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f4f4f5" strokeWidth="3" />
                  {data.total > 0 && <>
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#22c55e" strokeWidth="3"
                      strokeDasharray={`${(data.done / data.total) * 100} ${100 - (data.done / data.total) * 100}`} strokeDashoffset="0" />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#3b82f6" strokeWidth="3"
                      strokeDasharray={`${(data.in_progress / data.total) * 100} ${100 - (data.in_progress / data.total) * 100}`} strokeDashoffset={`${-(data.done / data.total) * 100}`} />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f59e0b" strokeWidth="3"
                      strokeDasharray={`${(data.review / data.total) * 100} ${100 - (data.review / data.total) * 100}`} strokeDashoffset={`${-((data.done + data.in_progress) / data.total) * 100}`} />
                  </>}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-['Outfit'] text-lg font-black text-zinc-900">{completionPct}%</span>
                </div>
              </div>
              <div className="flex-1 space-y-2">
                <StatusBar label="Done" count={data.done} total={data.total} color="#22c55e" />
                <StatusBar label="In Progress" count={data.in_progress} total={data.total} color="#3b82f6" />
                <StatusBar label="Review" count={data.review} total={data.total} color="#f59e0b" />
                <StatusBar label="To Do" count={data.todo} total={data.total} color="#a1a1aa" />
                {(data.custom_status_counts || []).map(cs => (
                  <StatusBar key={cs.id} label={cs.label} count={cs.count} total={data.total} color={cs.color} />
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
              <span className="flex items-center gap-1.5 text-zinc-500"><Circle size={8} weight="fill" className="text-emerald-500" /> Done {data.done}</span>
              <span className="flex items-center gap-1.5 text-zinc-500"><Circle size={8} weight="fill" className="text-blue-500" /> In Progress {data.in_progress}</span>
              <span className="flex items-center gap-1.5 text-zinc-500"><Circle size={8} weight="fill" className="text-amber-500" /> Review {data.review}</span>
              <span className="flex items-center gap-1.5 text-zinc-500"><Circle size={8} weight="fill" className="text-zinc-400" /> To Do {data.todo}</span>
              {(data.custom_status_counts || []).map(cs => (
                <span key={cs.id} className="flex items-center gap-1.5 text-zinc-500">
                  <Circle size={8} weight="fill" style={{ color: cs.color }} /> {cs.label} {cs.count}
                </span>
              ))}
            </div>
          </div>

          {/* Priority & Types */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-zinc-200/80 p-5">
              <h3 className="text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold mb-3">Priority Breakdown</h3>
              <div className="space-y-2">
                <StatusBar label="Critical" count={data.priority.critical} total={data.total} color="#dc2626" />
                <StatusBar label="High" count={data.priority.high} total={data.total} color="#f97316" />
                <StatusBar label="Medium" count={data.priority.medium} total={data.total} color="#eab308" />
                <StatusBar label="Low" count={data.priority.low} total={data.total} color="#3b82f6" />
              </div>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200/80 p-5">
              <h3 className="text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold mb-3">Types of Work</h3>
              <div className="flex gap-4">
                {[
                  { type: 'task', count: data.types.task, color: 'bg-blue-50 border-blue-200 text-blue-700' },
                  { type: 'story', count: data.types.story, color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                  { type: 'bug', count: data.types.bug, color: 'bg-red-50 border-red-200 text-red-700' },
                ].map(t => (
                  <div key={t.type} className={`flex-1 rounded-lg border p-3 text-center ${t.color}`}>
                    <div className="flex justify-center mb-1"><WorkTypeIcon type={t.type} size={20} /></div>
                    <p className="font-['Outfit'] text-xl font-black">{t.count}</p>
                    <p className="text-[10px] uppercase font-bold tracking-wide">{t.type === 'task' ? 'Tasks' : t.type === 'story' ? 'Stories' : 'Bugs'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Activity summary + Workload */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Last 7 days */}
          <div className="bg-white rounded-xl border border-zinc-200/80 p-5">
            <h3 className="text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold mb-4">Last 7 Days</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Created', value: data.created_recent, color: 'text-blue-600' },
                { label: 'Completed', value: data.completed_recent, color: 'text-emerald-600' },
                { label: 'Updated', value: data.updated_recent, color: 'text-violet-600' },
              ].map(s => (
                <div key={s.label} className="text-center py-3 rounded-lg bg-zinc-50">
                  <p className={`font-['Outfit'] text-2xl font-black ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] text-zinc-500 uppercase font-bold tracking-wide mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Team Workload */}
          <div className="bg-white rounded-xl border border-zinc-200/80 p-5">
            <h3 className="text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold mb-3">Team Workload</h3>
            {data.workload.length === 0 ? (
              <p className="text-[12px] text-zinc-400 text-center py-4">No assigned tasks yet</p>
            ) : (
              <div className="space-y-2.5">
                {data.workload.map(w => (
                  <div key={w.user_id} className="flex items-center gap-3">
                    <Avatar className="h-6 w-6 flex-shrink-0">
                      <AvatarImage src={w.avatar} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-400 to-indigo-500 text-white text-[9px] font-bold">{w.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="text-[12px] text-zinc-700 font-medium w-24 truncate">{w.name}</span>
                    <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: `${data.total > 0 ? (w.total / data.total) * 100 : 0}%` }} />
                    </div>
                    <span className="text-[11px] text-zinc-500 font-mono w-12 text-right">{w.done}/{w.total}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
